import {
  fetchActivePlans,
  fetchApp,
  fetchServiceSizingPoliciesByServiceId,
  selectAppById,
  selectEnvironmentById,
  selectFirstActivePlan,
  selectServiceSizingPolicyByServiceId,
  selectStackById,
} from "@app/deploy";
import { selectOrganizationSelectedId } from "@app/organizations";
import { useQuery, useSelector } from "@app/react";
import {
  appServiceScaleV2AutoscalingPathUrl,
  appServiceScaleV2ManualPathUrl,
} from "@app/routes";
import cn from "classnames";
import { NavLink, Outlet, useParams } from "react-router-dom";
import { LastScaleBanner, OnOffLabel } from "../shared";

const scaleTabClass = (isActive: boolean) =>
  cn(
    "flex-1 flex items-center justify-center gap-2 h-8 rounded-md border",
    "font-medium whitespace-nowrap hover:no-underline",
    isActive
      ? "bg-white border-black-100 text-black shadow-sm"
      : "bg-transparent border-transparent text-black-500 hover:text-black",
  );

/**
 * Chrome shared by the two Scale v2 pages: the last-scale banner and the
 * centered segmented control routing between Manual Scale and Autoscaling.
 * Only the Autoscaling tab gets the ON/OFF label.
 */
export const ServiceScaleV2Layout = () => {
  const { id = "", serviceId = "" } = useParams();
  const orgId = useSelector(selectOrganizationSelectedId);
  useQuery(fetchApp({ id }));
  useQuery(fetchServiceSizingPoliciesByServiceId({ serviceId }));
  useQuery(fetchActivePlans({ orgId }));
  const app = useSelector((s) => selectAppById(s, { id }));
  const environment = useSelector((s) =>
    selectEnvironmentById(s, { id: app.environmentId }),
  );
  const stack = useSelector((s) =>
    selectStackById(s, { id: environment.stackId }),
  );
  const policy = useSelector((s) =>
    selectServiceSizingPolicyByServiceId(s, { id: serviceId }),
  );
  const activePlan = useSelector(selectFirstActivePlan);
  const autoscalingAvailable =
    stack.horizontalAutoscaling ||
    activePlan.horizontalAutoscaling ||
    stack.verticalAutoscaling ||
    activePlan.verticalAutoscaling;

  return (
    <div className="flex flex-col gap-4">
      <LastScaleBanner serviceId={serviceId} />

      {autoscalingAvailable ? (
        <div className="flex justify-center">
          <nav
            aria-label="Scaling mode"
            className="flex w-full max-w-lg p-1 rounded-lg bg-black-50"
          >
            <NavLink
              to={appServiceScaleV2ManualPathUrl(id, serviceId)}
              className={({ isActive }) => scaleTabClass(isActive)}
            >
              Manual Scale
            </NavLink>
            <NavLink
              to={appServiceScaleV2AutoscalingPathUrl(id, serviceId)}
              className={({ isActive }) => scaleTabClass(isActive)}
            >
              Autoscaling
              <OnOffLabel on={policy.scalingEnabled} />
            </NavLink>
          </nav>
        </div>
      ) : null}

      <Outlet />
    </div>
  );
};
